import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/react'
import { useAdminAuthStore } from '../store/adminAuthStore'
import { toast } from '../store/toastStore'

// Separate axios instance for platform-admin routes
export const adminApi = axios.create({
  baseURL: '/api/v1/admin',
  headers: { 'Content-Type': 'application/json' },
})

// Authenticated non-/admin routes (tenant lifecycle lives under /api/v1/tenants).
export const platformApi = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete'])
interface RetryConfig extends InternalAxiosRequestConfig { _adminRetry?: boolean }
interface TokenPair { access_token: string; refresh_token: string; expires_in: number }

let refreshPromise: Promise<TokenPair> | null = null

async function refreshSession(): Promise<TokenPair> {
  const refreshToken = useAdminAuthStore.getState().refreshToken
  if (!refreshToken) throw new Error('No admin refresh token')

  if (!refreshPromise) {
    refreshPromise = axios
      .post<TokenPair>('/api/v1/admin/auth/refresh', { refresh_token: refreshToken })
      .then((response) => {
        const tokens = response.data
        useAdminAuthStore.getState().updateTokens(tokens.access_token, tokens.refresh_token)
        return tokens
      })
      .finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

function endSession() {
  useAdminAuthStore.getState().logout()
  if (window.location.pathname !== '/admin/login') window.location.href = '/admin/login'
}

function attachAdminInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    const token = useAdminAuthStore.getState().token
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  instance.interceptors.response.use(
    (res: AxiosResponse) => {
      const method = res.config.method?.toLowerCase() ?? ''
      if (MUTATION_METHODS.has(method) && res.data?.message) toast.success(res.data.message)
      return res
    },
    async (err: AxiosError<{ message?: string; error?: string }>) => {
      const config = err.config as RetryConfig | undefined
      const url = config?.url ?? ''
      const canRefresh = err.response?.status === 401 &&
        !config?._adminRetry &&
        !url.includes('/auth/login') &&
        !url.includes('/auth/refresh')

      if (canRefresh && config) {
        config._adminRetry = true
        try {
          const tokens = await refreshSession()
          config.headers.Authorization = `Bearer ${tokens.access_token}`
          return instance.request(config)
        } catch {
          endSession()
          toast.error('Your admin session expired. Please sign in again.')
          return Promise.reject(err)
        }
      }

      if (err.response?.status === 401 && !url.includes('/auth/login')) {
        endSession()
      } else {
        const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Something went wrong'
        toast.error(errMsg)
      }

      if (err.response && err.response.status >= 500) {
        Sentry.captureException(err, { tags: { api_url: url, status: err.response.status } })
      }
      return Promise.reject(err)
    },
  )
}

attachAdminInterceptors(adminApi)
attachAdminInterceptors(platformApi)

/** Revoke the server-side refresh token before clearing local admin state. */
export async function logoutPlatformAdmin() {
  const { refreshToken, logout } = useAdminAuthStore.getState()
  try {
    if (refreshToken) {
      await axios.post('/api/v1/admin/auth/logout', { refresh_token: refreshToken }, { timeout: 5000 })
    }
  } catch (err) {
    // Local logout must still succeed if the token is already expired or the service is down.
    Sentry.captureException(err, { tags: { operation: 'admin_logout' } })
  } finally {
    logout()
  }
}
